import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Inbox, Loader2, type LucideIcon } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { Card, CardContent } from '@/ui/base/card';
import { EmptyState } from '@/ui/EmptyState';

export interface CampoDoCard {
  label?: string;
  value: ReactNode;
  /** Quando true, ocupa toda a largura da linha. */
  full?: boolean;
}

export interface ItemDoCard {
  id: string;
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  fields?: CampoDoCard[];
  actions?: ReactNode;
  onClick?: () => void;
  /** Classe CSS adicional do card (ex.: destaque de SLA). */
  cardClassName?: string;
}

export interface MobileCardListProps {
  items: ItemDoCard[];
  isLoading?: boolean;
  emptyIcon?: LucideIcon;
  emptyTitle?: string;
  emptyDescription?: string;
  className?: string;
}

/**
 * Lista mobile-first em formato de card — cópia estrutural do protótipo
 * (`MobileCardList.tsx`). Use em listagens onde a tabela horizontal fica
 * difícil de operar abaixo de 640px: escondendo a `<DataTable>` em
 * `hidden sm:block` e mostrando este componente em `sm:hidden`, a tela
 * cumpre a regra do CLAUDE.md de responsividade a partir de 375px sem
 * rolagem horizontal.
 *
 * TECLADO (regra de interface 5). Cartão com `onClick` é alcançado por Tab e
 * aberto por Enter ou Espaço: ganha `role="button"` e `tabIndex={0}`, como a
 * linha clicável da `DataTable` e o `StatCard`. A tecla só vale no próprio
 * cartão — Enter num controle de dentro dele não abre o cartão.
 *
 * EXCEÇÃO: cartão que já traz um controle focável por dentro (um `<Link>` no
 * título, um botão ou caixa de seleção em `actions`) NÃO vira botão — seriam
 * dois controles aninhados, `nested-interactive` para o axe. O caminho de
 * teclado é então esse controle, e o mouse continua abrindo pelo cartão
 * inteiro. É a mesma regra de `linhaComElementoInterativo` da `DataTable`,
 * só que conferida no próprio cartão depois de montado, sem prop a mais.
 * Sem `onClick`, nada muda.
 */
export function MobileCardList({
  items,
  isLoading,
  emptyIcon = Inbox,
  emptyTitle = 'Nenhum registro',
  emptyDescription,
  className,
}: MobileCardListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-6">
          <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription} />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {items.map((item) => (
        <CartaoDaLista key={item.id} item={item} />
      ))}
    </div>
  );
}

/** Seletor dos controles que, dentro do cartão, já são alvo de teclado. */
const CONTROLE_FOCAVEL =
  'a[href],button,input,select,textarea,[role="button"],[role="checkbox"],[role="link"],[tabindex]:not([tabindex="-1"])';

function CartaoDaLista({ item }: { item: ItemDoCard }) {
  const cartao = useRef<HTMLDivElement>(null);
  const [temControleDentro, setTemControleDentro] = useState(false);

  // Conferido a cada troca do item: o conteúdo do cartão (título, ações) pode
  // ganhar ou perder um controle entre renderizações. Roda antes da pintura,
  // então o papel errado nunca chega à tela.
  useLayoutEffect(() => {
    const raiz = cartao.current;
    const achou = Boolean(raiz?.querySelector(CONTROLE_FOCAVEL));
    setTemControleDentro((atual) => (atual === achou ? atual : achou));
  }, [item]);

  const ehBotao = Boolean(item.onClick) && !temControleDentro;

  function aoTeclar(evento: KeyboardEvent<HTMLDivElement>) {
    if (evento.target !== evento.currentTarget) return;
    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      item.onClick?.();
    }
  }

  return (
    <Card
      ref={cartao}
      className={cn(
        'rounded-2xl transition-colors',
        item.onClick && 'cursor-pointer hover:bg-muted/50 active:bg-muted',
        ehBotao &&
          'focus-visible:ring-ring focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',
        item.cardClassName,
      )}
      onClick={item.onClick}
      role={ehBotao ? 'button' : undefined}
      tabIndex={ehBotao ? 0 : undefined}
      onKeyDown={ehBotao ? aoTeclar : undefined}
    >
      <CardContent className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{item.title}</div>
            {item.subtitle && (
              <div className="mt-0.5 truncate text-xs text-muted-foreground">{item.subtitle}</div>
            )}
          </div>
          {item.badges && (
            <div className="flex shrink-0 flex-wrap items-center gap-1">{item.badges}</div>
          )}
        </div>

        {item.fields && item.fields.length > 0 && (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            {item.fields.map((campo, indice) => (
              <div key={indice} className={cn(campo.full && 'col-span-2')}>
                {campo.label && (
                  <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {campo.label}
                  </dt>
                )}
                <dd className="truncate font-medium">{campo.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {item.actions && (
          <div
            className="flex flex-wrap items-center gap-2 pt-1"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {item.actions}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MobileCardList;
